/**
 * useChatGreeting
 *
 * `requestGreeting` extracted verbatim from useChat.ts (Stage 3). The greeting
 * refs stay in the orchestrator (they're also read/written by the session-switch
 * effect, sendMessage, and stopStreaming) and are threaded in as explicit
 * arguments so this hook can mutate them by reference exactly as the inline
 * implementation did. The useCallback + its dependency array are byte-identical
 * to the original, and the hook is called at the SAME body position so React's
 * hook order is unchanged. The 4-attempt SSE retry loop, abort handling, and
 * fire-and-forget greeting persistence are preserved verbatim.
 */
import { useCallback } from 'react'
import { saveConversationChatMessage, openGreetingStream } from '@/lib/api/endpoints/chat'
import { normalizeQuickActions } from '../utils/hidden-directives'
import type { ChatMessage, QuickAction } from './useChat.types'

interface UseChatGreetingArgs {
  token: string | null
  workspaceId: string
  isStreaming: boolean
  streamingMessage: ChatMessage | null
  setStreamingMessage: React.Dispatch<React.SetStateAction<ChatMessage | null>>
  setIsStreaming: React.Dispatch<React.SetStateAction<boolean>>
  setPendingQuickActions: React.Dispatch<React.SetStateAction<QuickAction[]>>
  currentSessionIdRef: React.MutableRefObject<string | undefined>
  greetingInProgressRef: React.MutableRefObject<boolean>
  greetingLastRequestedRef: React.MutableRefObject<Map<string, number>>
  greetingCancelledRef: React.MutableRefObject<boolean>
  greetingAbortControllerRef: React.MutableRefObject<AbortController | null>
}

export type RequestGreeting = (
  conversationId: string,
  isReturning?: boolean,
  hoursSinceLastMessage?: number,
  initialContent?: string,
  isOnboarding?: boolean,
) => Promise<void>

export function useChatGreeting({
  token,
  workspaceId,
  isStreaming,
  streamingMessage,
  setStreamingMessage,
  setIsStreaming,
  setPendingQuickActions,
  currentSessionIdRef,
  greetingInProgressRef,
  greetingLastRequestedRef,
  greetingCancelledRef,
  greetingAbortControllerRef,
}: UseChatGreetingArgs): { requestGreeting: RequestGreeting } {
  /**
   * Request a personalized greeting from Xavior
   * Greetings are ephemeral (shown but not saved to chat history)
   */
  const requestGreeting = useCallback(
    async (conversationId: string, isReturning: boolean = false, hoursSinceLastMessage?: number, initialContent?: string, isOnboarding?: boolean) => {
      // Synchronous guard — prevents concurrent calls even before isStreaming state updates
      if (greetingInProgressRef.current) return
      // Time-based dedup: reject if we already sent a greeting for this conversation
      // within the last 5 seconds (guards against effect re-fires after greetingInProgressRef resets)
      const now = Date.now()
      const lastRequested = greetingLastRequestedRef.current.get(conversationId)
      if (lastRequested && now - lastRequested < 5000) return
      greetingInProgressRef.current = true
      greetingLastRequestedRef.current.set(conversationId, now)
      // Don't request greeting if already streaming
      if (isStreaming) { greetingInProgressRef.current = false; return }
      // Don't request a new greeting if a completed greeting is still showing in streamingMessage.
      // Prevents duplicate greetings caused by HMR clearing the module-level greetedConversationIds
      // Set while the first greeting is still visible (streamingMessage not yet cleared by user send).
      if (streamingMessage?.routing === 'greeting') { greetingInProgressRef.current = false; return }

      // Only update state if this greeting still belongs to the active conversation.
      // Prevents a slow/aborted greeting from a previous conversation from resetting
      // isStreaming or streamingMessage after the user has switched to a new one.
      const isConversationActive = () => currentSessionIdRef.current === conversationId

      // Create ephemeral greeting message — lives in streamingMessage, never in messages
      // array, so a session-change fetch completing can't wipe it.
      const greetingMessage: ChatMessage = {
        id: `greeting-${Date.now()}`,
        role: 'assistant',
        content: initialContent ?? '',
        timestamp: new Date(),
        routing: 'greeting',
        streaming: true,
      } as ChatMessage

      // Show instant text immediately (suppresses "Thinking..." when initialContent provided)
      if (isConversationActive()) setStreamingMessage(greetingMessage)

      // SSE streaming now works on both web and Capacitor native.
      // On native, the SSE client (sse.ts) uses the original (unpatched) window.fetch
      // to bypass CapacitorHttp and get real ReadableStream support.

      try {
        setIsStreaming(true)
        // Reset cancel flag when starting a new greeting
        greetingCancelledRef.current = false

        // Retry up to 3 times on transient failures (502 during container startup, network blips).
        for (let attempt = 1; attempt <= 4; attempt++) {
          // Fresh abort controller per attempt so a previous abort signal doesn't block the retry
          greetingAbortControllerRef.current = new AbortController()
          const signal = greetingAbortControllerRef.current.signal

          // Get user's timezone
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

          // Reset stale-stream timer so isStreamingStalled counts from SSE start, not message creation
          if (isConversationActive()) {
            setStreamingMessage({ ...greetingMessage, timestamp: new Date() })
          }

          try {
            // Start streaming greeting
            let greetingContent = ''
            const greetingActions: QuickAction[] = []
            for await (const event of openGreetingStream(token, {
              conversationId,
              workspaceId,
              timezone,
              isReturning,
              hoursSinceLastMessage,
              isOnboarding,
            }, signal)) {
              // Check if greeting was cancelled (user sent a message) or conversation changed
              if (greetingCancelledRef.current || !isConversationActive()) {
                break // Stop processing greeting events
              }

              // Narrow type based on event.type, then cast event.data to the appropriate payload
              if (event.type === 'text') {
                const data = event.data as { text?: string; content?: string }
                const chunk = data.text || data.content || ''
                if (chunk) {
                  greetingContent += chunk
                  // Update streaming message — include fresh timestamp so isStreamingStalled
                  // resets with each chunk (prevents stale indicator while text is flowing)
                  if (!greetingCancelledRef.current && isConversationActive()) {
                    setStreamingMessage({
                      ...greetingMessage,
                      content: greetingContent,
                      timestamp: new Date(),
                    })
                  }
                }
              } else if (event.type === 'ui-action') {
                // Directive events emitted by greeting.ts — currently only 'actions' is
                // consumed here (chips shown to the user). The onboarding.complete directive
                // is handled elsewhere in the app via onUiActions in the main chat flow;
                // we don't duplicate that path for the greeting stream.
                const action = event.data as { type?: string; actions?: unknown }
                if (action?.type === 'actions' && Array.isArray(action.actions)) {
                  const chips = normalizeQuickActions(action.actions)
                  if (chips.length > 0) greetingActions.push(...chips)
                }
              } else if (event.type === 'complete') {
                // Greeting complete - mark as not streaming so it persists when user sends message.
                // Fall back to initialContent if SSE delivered no text (prevents wiping the
                // instant greeting that was already showing).
                const finalContent = greetingContent || greetingMessage.content
                // Chips are only surfaced when the agent explicitly emits them via
                // XAVIOR_ACTIONS. The text input handles free-form answers (name,
                // profession, etc.) where generic [Yes/No/Tell me more] fallbacks
                // would be nonsensical, so no fallback chip set is wired here.
                const finalChips = greetingActions
                if (!greetingCancelledRef.current && isConversationActive()) {
                  if (finalChips.length > 0) setPendingQuickActions(finalChips)
                  setStreamingMessage({
                    ...greetingMessage,
                    content: finalContent,
                    streaming: false,
                  } as ChatMessage)
                  // Persist greeting to DB so the agent has context when user replies
                  // (e.g. "jack" arrives with history [greeting, jack] → agent knows it's a name)
                  if (finalContent && token) {
                    saveConversationChatMessage(token, conversationId, {
                      role: 'assistant',
                      content: finalContent,
                      routing: 'greeting',
                    }).catch(() => { /* non-critical */ })
                  }
                }
              } else if (event.type === 'error') {
                const data = event.data as { message?: string }
                console.warn('[useChat] Greeting error:', data.message || event.data)
              }
            }
            break // Streaming completed successfully — stop retrying
          } catch (err) {
            if (err instanceof Error && err.name === 'AbortError') {
              console.log('[useChat] Greeting request aborted by user')
              break // User cancelled — don't retry
            }
            if (attempt < 4 && !greetingCancelledRef.current && isConversationActive()) {
              console.log(`[useChat] Greeting attempt ${attempt} failed, retrying in 2s...`, err)
              await new Promise(r => setTimeout(r, 2000))
              // loop continues to next attempt
            } else {
              console.warn('[useChat] Failed to request greeting:', err)
              break
            }
          }

          if (greetingCancelledRef.current || !isConversationActive()) break
        }
      } finally {
        greetingAbortControllerRef.current = null
        greetingInProgressRef.current = false
        // Only reset streaming state if this greeting still owns the current conversation.
        // If the user switched away, the new conversation's requestGreeting owns isStreaming.
        if (isConversationActive()) {
          setIsStreaming(false)
          // Clear stale greeting streamingMessage if it has no content
          // (prevents an empty AI bubble from lingering after a failed greeting)
          setStreamingMessage((current) => {
            if (current && current.routing === 'greeting' && !current.content?.trim()) {
              return null
            }
            return current
          })
        }
      }
    },
    [token, isStreaming, workspaceId],
  )

  return { requestGreeting }
}
